// src/components/moderation/ReportContentModal.tsx
import React, { useState } from 'react';
import { View, Text, TextInput, Pressable } from 'react-native';
import { AppModal, ModalAction } from '@/components/modal';
import { useThemeColors } from '@/lib/useThemeColors';
import { handleError } from '@/lib/errors';
import { useReportContent, type ReportReason, type ReportTarget } from '@/lib/moderation/api';

const REASONS: { key: ReportReason; label: string }[] = [
  { key: 'harassment', label: 'Harassment' },
  { key: 'inappropriate', label: 'Inappropriate content' },
  { key: 'spam', label: 'Spam' },
  { key: 'other', label: 'Something else' },
];

interface ReportContentModalProps {
  visible: boolean;
  onClose: () => void;
  target: { type: ReportTarget; id: string } | null;
}

export function ReportContentModal({ visible, onClose, target }: ReportContentModalProps) {
  const colors = useThemeColors();
  const report = useReportContent();
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<boolean>(false);

  const close = () => {
    setReason(null);
    setDetails('');
    setError(null);
    setDone(false);
    onClose();
  };

  const submit = async () => {
    if (!target || !reason) return;
    try {
      await report.mutateAsync({ targetType: target.type, targetId: target.id, reason, details: details.trim() || undefined });
      setDone(true);
    } catch (err) {
      const { message, isCancelled } = handleError(err, { context: 'ReportContentModal.submit' });
      if (!isCancelled) setError(message);
    }
  };

  return (
    <AppModal
      visible={visible}
      onClose={close}
      title={done ? 'Report received' : 'Report'}
      footer={
        <AppModal.Actions>
          {done ? (
            <ModalAction label="Done" variant="primary" onPress={close} />
          ) : (
            <>
              <ModalAction label="Cancel" variant="neutral" onPress={close} />
              <ModalAction label="Submit report" variant="destructive" onPress={submit} disabled={!reason || report.isPending} />
            </>
          )}
        </AppModal.Actions>
      }
    >
      {done ? (
        <Text numberOfLines={3} style={{ color: colors.text.secondary }}>
          Thanks. Our team reviews every report within 24 hours and removes content or accounts that break our terms.
        </Text>
      ) : (
        <View style={{ gap: 10 }}>
          {REASONS.map((r) => (
            <View
              key={`reason-${r.key}`}
              style={{
                height: 44,
                borderRadius: 12,
                borderWidth: 1,
                overflow: 'hidden',
                borderColor: reason === r.key ? colors.primary[500] : colors.border.light,
                backgroundColor: reason === r.key ? colors.primary[50] : colors.surface.primary,
              }}
            >
              <Pressable
                style={{ flex: 1, justifyContent: 'center', paddingHorizontal: 14 }}
                onPress={() => setReason(r.key)}
                accessibilityRole="radio"
                accessibilityState={{ selected: reason === r.key }}
              >
                <Text numberOfLines={1} style={{ color: colors.text.primary }}>
                  {r.label}
                </Text>
              </Pressable>
            </View>
          ))}
          <TextInput
            value={details}
            onChangeText={setDetails}
            maxLength={1000}
            multiline
            placeholder="Anything we should know? (optional)"
            placeholderTextColor={colors.text.tertiary}
            style={{ minHeight: 80, borderRadius: 12, borderWidth: 1, borderColor: colors.border.light, padding: 12, color: colors.text.primary }}
          />
          {error ? (
            <Text numberOfLines={2} style={{ color: colors.error }}>
              {error}
            </Text>
          ) : null}
        </View>
      )}
    </AppModal>
  );
}
